import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import ProfileCompletion from './components/ProfileCompletion';
import ProfileView from './components/ProfileView';
import LearningPathDashboard from './components/LearningPathDashboard';
import PracticePage from './components/PracticePage';
import SessionInterface from './components/SessionInterface';
import LandingPage from './components/LandingPage';
import MockTestSystem from './components/MockTestSystem';
import CollegePredictor from './components/CollegePredictor';
import AIPoweredLearning from './components/AIPoweredLearning';
import CETScorePredictor from './components/CETScorePredictor';

import HelpCenter from './components/HelpCenter';
import ContactUs from './components/ContactUs';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-800 mb-4">Something went wrong</h1>
            <p className="text-red-600 mb-4">Please refresh the page or try again later.</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, profileCompleted } = useAuth();

  console.log('ProtectedRoute state:', { user: !!user, loading, profileCompleted });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading session...</p>
          <p className="mt-2 text-sm text-gray-500">This may take a few moments</p>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log('No user, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  if (!profileCompleted) {
    console.log('Profile not completed, redirecting to profile setup');
    console.log('About to render ProfileCompletion component');
    return <Navigate to="/profile-setup" replace />;
  }

  console.log('User authenticated and profile completed, showing children');
  return children;
};

// Main App Component
const AppContent = () => {
  // Add environment debugging
  React.useEffect(() => {
    console.log('=== Environment Debug Info ===');
    console.log('NODE_ENV:', process.env.NODE_ENV);
    console.log('Current URL:', window.location.href);
    console.log('User Agent:', navigator.userAgent);
    console.log('Firebase Config:', {
      authDomain: "w3skill-exam-prep.firebaseapp.com",
      projectId: "w3skill-exam-prep"
    });
    console.log('=============================');
  }, []);

  return (
    <Router>
      <div className="App">
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10B981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 4000,
              iconTheme: {
                primary: '#EF4444',
                secondary: '#fff',
              },
            },
          }}
        />
        
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route 
            path="/profile" 
            element={<ProfileView />}
          />
          <Route 
            path="/profile-setup" 
            element={<ProfileCompletion />}
          />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <LearningPathDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/practice" 
            element={
              <ProtectedRoute>
                <PracticePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/session" 
            element={
              <ProtectedRoute>
                <SessionInterface />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/mock-tests" 
            element={
              <ProtectedRoute>
                <MockTestSystem />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/college-predictor" 
            element={
              <ProtectedRoute>
                <CollegePredictor />
              </ProtectedRoute>
            } 
          />
          <Route path="/ai-powered-learning" element={<AIPoweredLearning />} />
          <Route path="/cet-score-predictor" element={<CETScorePredictor />} />
          
          <Route path="/help-center" element={<HelpCenter />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/app" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </Router>
  );
};

// App with Auth Provider
const App = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
